import { CloseRounded } from '@mui/icons-material'
import { Box, Button, DialogContent, DialogTitle, FormControl, Grid, Input, MenuItem, Select, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import logo from '../../../../assets/Images/logos/logodark.svg'
import { toggleFormIntegrations, updateIntegration } from '../../../../redux/actions/integrations'
import { formValidator, resetFormValidator } from '../../../../redux/actions/validator'
import { IRootState } from '../../../../redux/types'
import { GridForm } from '../../styles/LoginRegisterStyle'
import { StyledDialog } from '../../styles/StyledDialog'
import ButtonBack from '../buttons/ButtonBack'
import { CancelButton, SaveButton } from '../inputs'

interface IIntegration {
  id: number
  name: null | string
  api_key: null | string
  token: null | string
  url: null | string
  status: null | boolean
}

function TokentModal({ stateModal }: any) {
  const { t } = useTranslation(['global'])
  const dispatch = useDispatch()

  const { integrationSelected } = useSelector((state: IRootState) => state.integration)
  const { isValidForm } = useSelector((state: IRootState) => state.validator)

  const [showLogin, setShowLogin] = useState(false)
  const [showRegistration, setShowRegistration] = useState(false)
  const [integration, setIntegration] = useState<IIntegration>({
    id: 0,
    name: null,
    api_key: null,
    token: null,
    url: null,
    status: null
  })

  const handleGoBack = () => {
    setShowRegistration(false)
    setShowLogin(false)
  }

  const handleInputChange = (inputName: string) => (event: any) => {
    const value = event.target.value
    setIntegration({ ...integration, [inputName]: value })
  }

  useEffect(() => {
    if (integrationSelected !== null && integrationSelected.id !== integration.id) {
      setIntegration({
        id: integrationSelected.id,
        name: integrationSelected.name,
        api_key: integrationSelected.api_key,
        token: integrationSelected.token,
        url: integrationSelected.url,
        status: integrationSelected.status
      })
    }
  }, [integrationSelected])

  useEffect(() => {
    if (isValidForm) {
      dispatch(resetFormValidator())
      dispatch(updateIntegration(integration))
    }
  }, [isValidForm])

  const handleSubmit = () => {
    dispatch(
      formValidator({
        api_key: [integration.api_key, 'required|max:45|min:2']
      })
    )
  }

  return (
    <StyledDialog
      open={stateModal}
      onClose={() => {
        dispatch(toggleFormIntegrations())
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">
        <Grid container alignItems="center" justifyContent={!showLogin && !showRegistration ? 'flex-end' : 'space-between'}>
          {!showLogin && !showRegistration ? (
            <Button
              onClick={() => {
                dispatch(toggleFormIntegrations())
              }}
              color="primary"
              variant="text"
            >
              <CloseRounded />
            </Button>
          ) : (
            <>
              <img src={logo} height="24" alt="logo" />
              <ButtonBack customFunction={handleGoBack} goBack={false} async={false} />
            </>
          )}
        </Grid>
      </DialogTitle>
      <DialogContent
        sx={theme => ({
          paddingBottom: theme.spacing(3),
          paddingTop: 0,
          '& h6': {
            textAlign: 'center',
            marginBottom: theme.spacing(2)
          },
          overflow: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            backgroundColor: theme.palette.grey[200],
            borderRadius: '4px',
            width: '8px',
            margin: '8px',
            '&-thumb': {
              backgroundColor: theme.palette.grey[500],
              borderRadius: '4px'
            }
          },
          [theme.breakpoints.down('sm')]: {
            padding: 0,
            paddingBottom: theme.spacing(3)
          }
        })}
      >
        <Grid container alignItems="center" spacing={2} justifyContent={!showLogin && !showRegistration ? 'flex-end' : 'space-between'}>
          <GridForm className="live">
            <Grid
              sx={theme => ({
                width: '100% !important',
                [theme.breakpoints.down('sm')]: {
                  width: 'auto !important',
                  '& .MuiGrid-item': {
                    padding: 1
                  }
                }
              })}
              container
              spacing={3}
            >
              <Grid item xs={12}>
                <Typography
                  sx={theme => ({
                    fontWeight: 700,
                    '&live': {
                      paddingBottom: 2,
                      paddingLeft: 2,
                      borderLeft: `4px solid ${theme.palette.primary.main}`
                    },
                    [theme.breakpoints.down('sm')]: {
                      fontSize: theme.typography.h6.fontSize
                    }
                  })}
                  className="live"
                  color="textPrimary"
                  variant="h4"
                >
                  Integración de {import.meta.env.VITE_NAME} con Zapier
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Box mb={0.5}>
                  <Typography>API Key</Typography>
                </Box>
                <FormControl variant="filled" fullWidth>
                  <Input placeholder={'API Key'} value={integration.api_key} onChange={handleInputChange('api_key')} />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box mb={0.5}>
                  <Typography>URL</Typography>
                </Box>
                <FormControl variant="filled" fullWidth>
                  <Input placeholder={'URL'} value={integration.url} onChange={handleInputChange('url')} />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box mb={0.5}>
                  <Typography>Token</Typography>
                </Box>
                <FormControl variant="filled" fullWidth>
                  <Input placeholder={'Token'} value={integration.token} onChange={handleInputChange('token')} />
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box mb={0.5}>
                  <Typography>Estado</Typography>
                </Box>
                <FormControl fullWidth variant="filled">
                  <Select
                    value={integration.status}
                    labelId="lbl-roles"
                    id="combo-box-roles"
                    fullWidth
                    label="rol"
                    onChange={handleInputChange('status')}
                  >
                    {['Inactivo', 'Activo'].map((status: string, key: number) => (
                      <MenuItem key={status} value={key}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" width="100%" justifyContent="center" gap={3}>
                  <CancelButton
                    sx={{ color: 'common.white', bgcolor: 'grey.300' }}
                    onClick={() => {
                      dispatch(toggleFormIntegrations())
                    }}
                  >
                    {t('cancel')}
                  </CancelButton>
                  <SaveButton
                    onClick={() => {
                      handleSubmit()
                    }}
                  >
                    {t('continue')}
                  </SaveButton>
                </Box>
              </Grid>
            </Grid>
          </GridForm>
        </Grid>
      </DialogContent>
    </StyledDialog>
  )
}

export default TokentModal
